/**
 * UnifiedUploadModal Component
 *
 * A modal wrapper for the new unified file upload system.
 * This is used when the VITE_USE_UNIFIED_UPLOAD feature flag is enabled.
 * It provides the same interface as the legacy UploadFileModal but uses
 * the new Uppy.js-based upload system with TUS protocol.
 *
 * Smart detection: when files are selected, the type dropdown is auto-filled
 * based on file extension + filename hints. The user can still change it.
 */

import React, { useState, useCallback, useRef } from 'react'
import Modal from '@/components/Modal'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTranslation } from '@/i18n'
import { colors } from '@/styles/colors'
import { apiService, type FileMetadata } from '@/service/api'
import { Info, Loader2 } from 'lucide-react'
import { ALL_SUPPORTED_EXTENSIONS } from '@/utils/fileValidation'
import DuplicateFileModal from '@/routes/DataCatalog/DuplicateFileModal'
import BulkDuplicateModal, { type DuplicateFile } from '@/routes/DataCatalog/BulkDuplicateModal'
import type { Confidence, SelectableFileType } from './fileTypeDetection'
import { toSelectableFileType } from './fileTypeDetection'
import { detectBatchFileTypesFromContent } from './contentTypeDetection'
import { ConfidenceBadge } from './ConfidenceBadge'

const ALL_ACCEPT_STRING = ALL_SUPPORTED_EXTENSIONS.map(ext => `.${ext}`).join(',')

interface UploadOptions {
  autoRename?: boolean
  replaceDocumentId?: string
  duplicateMap?: Map<string, string>
  skipNames?: Set<string>
}

interface PnidDuplicateOptions {
  autoRename?: boolean
  duplicateMap?: Map<string, string>
  isMultiPage?: boolean
  bypassAreaSelection?: boolean
}

interface UnifiedUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadComplete?: (fileId: string, fileName: string) => void
  onPnidPfdUpload?: (
    files: File[],
    fileType: 'pnid' | 'pfd',
    duplicateOptions?: PnidDuplicateOptions
  ) => void
  initialFiles?: File[]
  uploadCallbacks?: {
    // Mirrors ModalSliceState.uploadCallbacks.onStart so the call site
    // can pass docId + multiPageProps and consume the uploadStartedAt
    // return value. Previous narrow shape was a lint/type lie.
    onStart?: (
      fileName: string,
      documentType: string,
      options?: {
        fileSize?: number
        multiPageProps?: {
          multi_page_set_id: string
          sheet_index: string
          sheet_count: string
          multi_page_set_name: string
        }
        docId?: string
      },
    ) => string | void
    onComplete?: (fileName: string) => void
    onDiscard?: (docId: string) => void
    getAbortSignal?: (fileName: string) => AbortSignal | undefined
  }
}

export const UnifiedUploadModal: React.FC<UnifiedUploadModalProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  onPnidPfdUpload,
  initialFiles,
  uploadCallbacks,
}) => {
  const { t } = useTranslation()
  const [selectedFileType, setSelectedFileType] = useState<SelectableFileType | ''>('')
  const [isMultiPage, setIsMultiPage] = useState(false)
  const [bypassAreaSelection, setBypassAreaSelection] = useState(false)
  // Opt-in P&ID parsing for mixed-document uploads. Visible only when
  // selectedFileType === 'document'. OFF by default — not persisted across
  // uploads (a fresh modal starts OFF). When ON, the value rides through
  // FileMetadata.properties.parse_pnids to the BullMQ worker which decides
  // whether to run CASCADE classification + per-page P&ID parsing.
  //
  // Multi-file batch semantics: the checkbox state applies uniformly to
  // every file in the selected batch — there is no per-file toggle.
  // Three PDFs selected + checkbox ON ⇒ all three get parse_pnids=true.
  const [parsePnids, setParsePnids] = useState(false)
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Detection confidence badge (shown next to dropdown after auto-detect)
  const [detectedConfidence, setDetectedConfidence] = useState<Confidence | null>(null)

  // Duplicate modal state - single file
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateFileName, setDuplicateFileName] = useState('')
  const [duplicateExistingId, setDuplicateExistingId] = useState<string | null>(null)

  // Duplicate modal state - bulk
  const [showBulkDuplicateModal, setShowBulkDuplicateModal] = useState(false)
  const [duplicateFiles, setDuplicateFiles] = useState<DuplicateFile[]>([])
  const [newFilesCount, setNewFilesCount] = useState(0)
  const [duplicateMap, setDuplicateMap] = useState<Map<string, string>>(new Map())

  // Pending files waiting for duplicate resolution
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  // Track whether pending files are PFD/P&ID (need region selection) or regular (direct upload)
  const [pendingPnidFileType, setPendingPnidFileType] = useState<'pnid' | 'pfd' | null>(null)

  // Guard: prevent the drag-drop auto-upload effect from re-firing on callback reference changes
  const hasAutoUploadedRef = useRef(false)

  // Tracks whether the current selectedFileType was set by auto-detection (true)
  // or by the user picking from the dropdown (false). Auto-detected types can be
  // overridden by a subsequent auto-detection (e.g. user picks different files),
  // but a manual choice is sticky until the user changes it again.
  const [wasTypeAutoDetected, setWasTypeAutoDetected] = useState(false)

  // Reset multi-page and file input only when user manually changes the dropdown.
  // Auto-detection type changes must NOT reset isMultiPage, otherwise the
  // multi-page P&ID flow breaks (selecting files resets the checkbox).
  const isManualTypeChangeRef = useRef(false)
  React.useEffect(() => {
    if (isManualTypeChangeRef.current) {
      isManualTypeChangeRef.current = false
      setIsMultiPage(false)
      setBypassAreaSelection(false)
      setParsePnids(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [selectedFileType])

  // Clear state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFileType('')
      setIsMultiPage(false)
      setBypassAreaSelection(false)
      setParsePnids(false)
      setWasTypeAutoDetected(false)
      setIsCheckingDuplicates(false)
      setDetectedConfidence(null)
      setShowDuplicateModal(false)
      setShowBulkDuplicateModal(false)
      setPendingFiles([])
      setPendingPnidFileType(null)
      setDuplicateFiles([])
      setDuplicateMap(new Map())
      hasAutoUploadedRef.current = false
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [isOpen])

  /**
   * Upload files with optional duplicate handling options.
   * Used for non-PFD file types (document, timeseries).
   */
  const uploadFiles = useCallback(async (files: File[], options?: UploadOptions, overrideType?: string) => {
    const effectiveType = overrideType || selectedFileType
    const filesToUpload = files.filter(file => !options?.skipNames?.has(file.name))

    // FS-499: pre-register every file in the batch server-side BEFORE
    // creating local temp catalog rows. The returned docIds back the
    // local rows, so a page refresh mid-upload leaves pending rows in
    // Postgres (catalog can show them on reload) instead of silently
    // dropping the queue. Mirrors the bypass-area-selection path in
    // ModalProvider for P&ID/PFD. Falls back to legacy local-only on
    // API failure so a transient blip doesn't block the upload.
    const batchId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
    const docIdByName = new Map<string, string>()
    try {
      const resp = await apiService.registerPendingFiles(
        batchId,
        filesToUpload.map(f => {
          const replaceId = options?.duplicateMap?.get(f.name) ?? options?.replaceDocumentId
          return {
            filename: f.name,
            fileType: effectiveType,
            fileSize: f.size,
            ...(replaceId
              ? { replaceDocumentId: replaceId }
              : { autoRename: options?.autoRename ?? true }),
          }
        }),
      )
      // Key by the SENT filename (not the server-resolved name) so the
      // upload loop below can look up the docId by the original name.
      resp.files.forEach((r, i) => {
        const sent = filesToUpload[i]?.name
        if (sent) docIdByName.set(sent, r.docId)
      })
    } catch (err) {
      console.warn('[FS-499] register-pending failed; falling back to legacy local-only flow', err)
    }

    const uploadTimestamps = new Map<string, string>()
    for (const file of filesToUpload) {
      if (uploadCallbacks?.onStart) {
        const docId = docIdByName.get(file.name)
        const result = uploadCallbacks.onStart(file.name, effectiveType, { fileSize: file.size, docId })
        if (typeof result === 'string') {
          uploadTimestamps.set(file.name, result)
        }
      }
    }

    // Close modal immediately — upload status will show on the data catalog page
    onClose()

    // Continue uploads in the background. Track aborted files so we
    // can discard their pre-registered server rows after the loop;
    // otherwise they'd sit forever in 'uploading' state.
    const abortedDocIds: string[] = []
    try {
      for (const file of filesToUpload) {
        try {
          const signal = uploadCallbacks?.getAbortSignal?.(file.name)
          if (signal?.aborted) {
            const docId = docIdByName.get(file.name)
            if (docId) abortedDocIds.push(docId)
            continue
          }

          const fileUploadOptions: { autoRename?: boolean; replaceDocumentId?: string } = {}
          if (options?.autoRename) {
            fileUploadOptions.autoRename = true
          }
          if (options?.replaceDocumentId) {
            fileUploadOptions.replaceDocumentId = options.replaceDocumentId
          }
          if (options?.duplicateMap?.has(file.name)) {
            fileUploadOptions.replaceDocumentId = options.duplicateMap.get(file.name)
          }

          // Multi-file batch semantics: parse_pnids applies uniformly to
          // every file. The checkbox is only visible/meaningful when the user
          // picked fileType='document'; for other types the field is
          // ignored by the backend (P&ID files always parse, etc.).
          const result = await apiService.uploadFile(file, {
            name: file.name,
            description: '',
            type: effectiveType as FileMetadata['type'],
            properties: {
              parse_pnids: parsePnids,
            },
          }, signal, {
            ...fileUploadOptions,
            uploadStartedAt: uploadTimestamps.get(file.name),
            docId: docIdByName.get(file.name),
          })

          if (onUploadComplete && result?.id) {
            onUploadComplete(result.id, file.name)
          }
        } catch (error) {
          console.error(`Upload failed for ${file.name}:`, error)
          // Mid-flight abort: if the signal fired while uploadFile was
          // awaiting (AbortError swallowed by this catch), the
          // pre-loop guard at the top of the iteration didn't see it.
          // Re-check here so the docId still gets discarded server-side
          // — without this, the pending row would zombie indefinitely.
          const lateSignal = uploadCallbacks?.getAbortSignal?.(file.name)
          if (lateSignal?.aborted) {
            const docId = docIdByName.get(file.name)
            if (docId) abortedDocIds.push(docId)
          }
        } finally {
          if (uploadCallbacks?.onComplete) {
            uploadCallbacks.onComplete(file.name)
          }
        }
      }
    } catch (error) {
      console.error('Upload failed:', error)
    }

    if (abortedDocIds.length > 0) {
      apiService
        .discardPendingFiles(batchId, abortedDocIds)
        .catch(err => console.error('[FS-499] discard-pending (UnifiedUpload abort) failed', err))
    }
  }, [selectedFileType, onUploadComplete, onClose, uploadCallbacks, parsePnids])

  /**
   * Send PFD/P&ID files to parent (ModalProvider) for region selection.
   */
  const sendToPnidUpload = useCallback((files: File[], fileType: 'pnid' | 'pfd', options?: PnidDuplicateOptions) => {
    if (onPnidPfdUpload) {
      onPnidPfdUpload(files, fileType, options)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setPendingFiles([])
    setPendingPnidFileType(null)
  }, [onPnidPfdUpload])

  // Pass multi-page and bypass flags when sending to P&ID upload
  const sendToPnidUploadWithMultiPage = useCallback((files: File[], fileType: 'pnid' | 'pfd', options?: PnidDuplicateOptions) => {
    const enhancedOptions = {
      ...options,
      isMultiPage: selectedFileType === 'pnid' && isMultiPage,
      bypassAreaSelection,
    }
    sendToPnidUpload(files, fileType, enhancedOptions)
  }, [selectedFileType, isMultiPage, bypassAreaSelection, sendToPnidUpload])

  /**
   * Check for duplicates then either upload or show modal.
   * Works for both PFD and non-PFD files.
   *
   * FS-487: previously fanned out N parallel `checkDuplicateDocument` calls
   * via `Promise.allSettled`, which (a) made N round trips with the
   * accompanying TLS/queueing overhead, and (b) waited for the slowest
   * promise — one stuck connection held the spinner indefinitely. Replaced
   * with `checkDuplicateDocumentsBulk` calls wrapped in AbortControllers
   * with a 30s hard deadline per request. On timeout/abort/error the caller
   * falls through to the existing fail-safe branch, which proceeds with
   * `autoRename: true` so the user is never stranded on the spinner.
   *
   * FS-487 follow-up: backend caps each call at 500 items, so we chunk the
   * input into 500-item batches and run them in parallel. Without chunking,
   * a 501+ file upload would 400 at the backend and silently fall through
   * to autoRename — skipping duplicate detection entirely.
   */
  const checkAndUploadFiles = useCallback(async (files: File[], pnidFileType?: 'pnid' | 'pfd', overrideType?: string) => {
    setIsCheckingDuplicates(true)

    const BULK_DUPLICATE_CHECK_CHUNK_SIZE = 500  // matches backend's per-call cap
    const BULK_DUPLICATE_CHECK_TIMEOUT_MS = 30_000

    const chunks: File[][] = []
    for (let i = 0; i < files.length; i += BULK_DUPLICATE_CHECK_CHUNK_SIZE) {
      chunks.push(files.slice(i, i + BULK_DUPLICATE_CHECK_CHUNK_SIZE))
    }

    const controllers = chunks.map(() => new AbortController())
    const timeoutIds = controllers.map((c) =>
      setTimeout(() => c.abort(), BULK_DUPLICATE_CHECK_TIMEOUT_MS),
    )

    try {
      const responses = await Promise.all(
        chunks.map((chunk, i) =>
          apiService.checkDuplicateDocumentsBulk(
            chunk.map((f) => ({ name: f.name })),
            controllers[i].signal,
          ),
        ),
      )

      // FS-487 hardening: backend's contract is one result row per input
      // item in input order. If any chunk's shape diverges from that
      // contract, fall through to autoRename for the whole batch rather
      // than silently mapping by index and treating collisions as
      // non-duplicates (which would surface as 409s at upload time).
      const flatResults: Array<{ name: string; exists: boolean; existing_document_id: string | null }> = []
      for (let i = 0; i < responses.length; i++) {
        const response = responses[i]
        if (!response?.results || response.results.length !== chunks[i].length) {
          console.warn(
            'Duplicate-check chunk shape mismatch, proceeding with auto-rename',
            { chunkIndex: i, expected: chunks[i].length, got: response?.results?.length },
          )
          if (pnidFileType) {
            sendToPnidUploadWithMultiPage(files, pnidFileType, { autoRename: true })
          } else {
            uploadFiles(files, { autoRename: true }, overrideType)
          }
          return
        }
        flatResults.push(...response.results)
      }

      const fulfilled = files.map((file, i) => {
        const row = flatResults[i]
        return {
          file,
          exists: !!row?.exists,
          existingDocumentId: row?.existing_document_id ?? null,
        }
      })

      const foundDuplicates = fulfilled.filter(r => r.exists && r.existingDocumentId)

      if (foundDuplicates.length === 0) {
        if (pnidFileType) {
          sendToPnidUploadWithMultiPage(files, pnidFileType)
        } else {
          uploadFiles(files, undefined, overrideType)
        }
        return
      }

      // Duplicates found — store pending state and show modal
      setPendingFiles(files)
      setPendingPnidFileType(pnidFileType || null)

      if (files.length === 1 && foundDuplicates.length === 1) {
        setDuplicateFileName(foundDuplicates[0].file.name)
        setDuplicateExistingId(foundDuplicates[0].existingDocumentId)
        setShowDuplicateModal(true)
      } else {
        const dupes: DuplicateFile[] = foundDuplicates.map(d => ({
          fileName: d.file.name,
          existingDocumentId: d.existingDocumentId!,
        }))
        const map = new Map<string, string>()
        foundDuplicates.forEach(d => {
          if (d.existingDocumentId) {
            map.set(d.file.name, d.existingDocumentId)
          }
        })

        setDuplicateFiles(dupes)
        setDuplicateMap(map)
        setNewFilesCount(files.length - foundDuplicates.length)
        setShowBulkDuplicateModal(true)
      }
    } catch (error) {
      // FS-487: AbortError fires when the 30s timeout fires (or anything
      // else cancels the signal). Either way, fall through to auto-rename
      // so the user is never stuck on the spinner.
      console.warn('Duplicate check failed, proceeding with auto-rename:', error)
      if (pnidFileType) {
        sendToPnidUploadWithMultiPage(files, pnidFileType, { autoRename: true })
      } else {
        uploadFiles(files, { autoRename: true }, overrideType)
      }
    } finally {
      timeoutIds.forEach(clearTimeout)
      setIsCheckingDuplicates(false)
    }
  }, [uploadFiles, sendToPnidUploadWithMultiPage])

  // Single file duplicate handlers
  const handleReplace = useCallback(async () => {
    setShowDuplicateModal(false)
    if (pendingPnidFileType) {
      const map = new Map<string, string>()
      if (duplicateExistingId) {
        map.set(pendingFiles[0]?.name, duplicateExistingId)
      }
      sendToPnidUploadWithMultiPage(pendingFiles, pendingPnidFileType, { duplicateMap: map })
    } else {
      await uploadFiles(pendingFiles, { replaceDocumentId: duplicateExistingId || undefined })
    }
  }, [pendingFiles, pendingPnidFileType, duplicateExistingId, uploadFiles, sendToPnidUploadWithMultiPage])

  const handleKeepBoth = useCallback(async () => {
    setShowDuplicateModal(false)
    if (pendingPnidFileType) {
      sendToPnidUploadWithMultiPage(pendingFiles, pendingPnidFileType, { autoRename: true })
    } else {
      await uploadFiles(pendingFiles, { autoRename: true })
    }
  }, [pendingFiles, pendingPnidFileType, uploadFiles, sendToPnidUploadWithMultiPage])

  // Bulk duplicate handlers
  const handleBulkReplace = useCallback(async () => {
    setShowBulkDuplicateModal(false)
    if (pendingPnidFileType) {
      sendToPnidUploadWithMultiPage(pendingFiles, pendingPnidFileType, { duplicateMap })
    } else {
      await uploadFiles(pendingFiles, { duplicateMap })
    }
  }, [pendingFiles, pendingPnidFileType, duplicateMap, uploadFiles, sendToPnidUploadWithMultiPage])

  const handleBulkKeepBoth = useCallback(async () => {
    setShowBulkDuplicateModal(false)
    if (pendingPnidFileType) {
      sendToPnidUploadWithMultiPage(pendingFiles, pendingPnidFileType, { autoRename: true })
    } else {
      await uploadFiles(pendingFiles, { autoRename: true })
    }
  }, [pendingFiles, pendingPnidFileType, uploadFiles, sendToPnidUploadWithMultiPage])

  const handleBulkSkip = useCallback(async () => {
    setShowBulkDuplicateModal(false)
    const skipNames = new Set(duplicateFiles.map(d => d.fileName))
    if (pendingPnidFileType) {
      const remaining = pendingFiles.filter(f => !skipNames.has(f.name))
      if (remaining.length > 0) {
        sendToPnidUploadWithMultiPage(remaining, pendingPnidFileType)
      } else {
        setPendingFiles([])
        setPendingPnidFileType(null)
      }
    } else {
      await uploadFiles(pendingFiles, { skipNames })
    }
  }, [pendingFiles, pendingPnidFileType, duplicateFiles, uploadFiles, sendToPnidUploadWithMultiPage])

  // Handle drag-dropped files: auto-detect type (content-refined) and set dropdown
  React.useEffect(() => {
    if (!(isOpen && initialFiles && initialFiles.length > 0) || hasAutoUploadedRef.current) return
    hasAutoUploadedRef.current = true
    let cancelled = false
    void (async () => {
      const detection = await detectBatchFileTypesFromContent(initialFiles)
      // Bail if the modal closed / deps changed while sniffing — a late
      // setSelectedFileType would re-trigger the auto-upload effect below.
      if (cancelled || !detection.dominantType) return
      setSelectedFileType(toSelectableFileType(detection.dominantType))
      setDetectedConfidence(detection.files[0]?.confidence ?? null)
    })()
    return () => { cancelled = true }
  }, [isOpen, initialFiles])

  // After drag-drop auto-detection sets the type, trigger upload once
  React.useEffect(() => {
    if (isOpen && initialFiles && initialFiles.length > 0 && selectedFileType && hasAutoUploadedRef.current) {
      // Clear the ref so subsequent dropdown changes don't re-trigger the upload
      hasAutoUploadedRef.current = false
      const pnidType = (selectedFileType === 'pnid' || selectedFileType === 'pfd')
        ? selectedFileType as 'pnid' | 'pfd'
        : undefined
      checkAndUploadFiles(initialFiles, pnidType)
    }
    // Only run when selectedFileType changes after drag-drop detection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFileType])

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)

    // Only skip re-detection when the user made a deliberate manual choice.
    // A previously auto-detected type can be overridden by a new detection
    // (e.g. user picks CSVs after previously picking PDFs), but a manual
    // dropdown selection is sticky to protect isMultiPage / bypassAreaSelection.
    const skipAutoDetect = !!selectedFileType && !wasTypeAutoDetected
    let effectiveType: string = selectedFileType
    if (!skipAutoDetect) {
      const detection = await detectBatchFileTypesFromContent(fileArray)
      if (detection.dominantType) {
        const detectedType = toSelectableFileType(detection.dominantType)
        setSelectedFileType(detectedType)
        setDetectedConfidence(detection.files[0]?.confidence ?? null)
        setWasTypeAutoDetected(true)
        effectiveType = detectedType
      }
    }

    if (!effectiveType) return

    const pnidType = (effectiveType === 'pnid' || effectiveType === 'pfd')
      ? effectiveType as 'pnid' | 'pfd'
      : undefined
    await checkAndUploadFiles(fileArray, pnidType, effectiveType)
  }, [selectedFileType, wasTypeAutoDetected, checkAndUploadFiles])

  // Clear confidence badge when user manually changes the dropdown
  const handleTypeChange = useCallback((value: string) => {
    isManualTypeChangeRef.current = true
    setSelectedFileType(value as SelectableFileType)
    setWasTypeAutoDetected(false)
    setDetectedConfidence(null) // User chose manually, no confidence to show
  }, [])

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('data_catalog:upload_data_title', 'Upload data')}
        showCloseButton
      >
        <div>
              {/* File Type Dropdown — auto-filled after file selection */}
              <div className="mb-4">
                <Label htmlFor="type">
                  {t('data_catalog:data_type_label')} <span style={{ color: colors.error[500] }}>*</span>
                </Label>
                <div className="flex items-center gap-3 mt-2">
                  <Select
                    name="type"
                    value={selectedFileType}
                    onValueChange={handleTypeChange}
                  >
                    <SelectTrigger className="w-full border-gray-300 dark:border-gray-600 focus:border-gray-400 focus:ring-0">
                      <SelectValue placeholder={t('data_catalog:data_type_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pnid">
                        {t('data_catalog:pnid_option')} (pdf, png, jpg)
                      </SelectItem>
                      <SelectItem value="pfd">
                        {t('data_catalog:pfd_option')} (pdf, png, jpg)
                      </SelectItem>
                      <SelectItem value="document">
                        {t('data_catalog:document_option')} (pdf, docx, pptx, txt, csv, xlsx)
                      </SelectItem>
                      <SelectItem value="timeseries">
                        {t('data_catalog:timeseries_option')} (csv, xlsx)
                      </SelectItem>
                      <SelectItem value="plc_program">
                        {t('data_catalog:plc_program_option')} (xml, TcPOU, st, scl, L5X)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Confidence badge — shown when type was auto-detected */}
                  {detectedConfidence && <ConfidenceBadge confidence={detectedConfidence} />}
                </div>
              </div>

              {/* Checkboxes and file input */}
              <div className="space-y-4">
                {/* Parse P&ID diagrams checkbox — only show for document type (mixed PDFs).
                    OFF by default. Direct pnid/pfd uploads always parse, so the checkbox
                    is irrelevant there. */}
                {selectedFileType === 'document' && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="parse-pnids"
                      checked={parsePnids}
                      onCheckedChange={(checked) => setParsePnids(checked === true)}
                      disabled={isCheckingDuplicates}
                    />
                    <Label
                      htmlFor="parse-pnids"
                      className="text-sm font-normal cursor-pointer"
                    >
                      {t('data_catalog:parse_pnids_label', 'Parse P&ID diagrams in this document')}
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          {t(
                            'data_catalog:parse_pnids_help',
                            'Off by default. Turn on to identify and process P&ID drawings inside the PDF. Adds several minutes per P&ID page.',
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}

                {/* Multi-page checkbox - only show for P&ID */}
                {selectedFileType === 'pnid' && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="multi-page"
                      checked={isMultiPage}
                      onCheckedChange={(checked) => setIsMultiPage(checked === true)}
                      disabled={isCheckingDuplicates}
                    />
                    <Label
                      htmlFor="multi-page"
                      className="text-sm font-normal cursor-pointer"
                    >
                      {t('data_catalog:multi_page_option', 'Multi-page')}
                    </Label>
                  </div>
                )}

                {/* Bypass Area Selection checkbox - only show for P&ID and PFD */}
                {(selectedFileType === 'pnid' || selectedFileType === 'pfd') && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="bypass-area-selection"
                      checked={bypassAreaSelection}
                      onCheckedChange={(checked) => setBypassAreaSelection(checked === true)}
                      disabled={isCheckingDuplicates}
                    />
                    <Label
                      htmlFor="bypass-area-selection"
                      className="text-sm font-normal cursor-pointer"
                    >
                      {t('data_catalog:bypass_area_selection', 'Bypass Area Selection')}
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {t('data_catalog:bypass_area_selection_tooltip', 'Skip region selection for faster batch uploads. Regions can be assigned later.')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}

                {/* File input */}
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALL_ACCEPT_STRING}
                    onChange={handleFileSelect}
                    disabled={isCheckingDuplicates}
                    multiple={true}
                    className="block w-full text-sm text-gray-500 dark:text-dark-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:cursor-pointer disabled:opacity-50"
                  />
                  {isCheckingDuplicates && (
                    <Loader2 className="h-5 w-5 animate-spin text-gray-500 dark:text-dark-text-muted" />
                  )}
                </div>
                {isCheckingDuplicates && (
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                    Checking for duplicates...
                  </p>
                )}
                <style>{`
                  input[type="file"]::file-selector-button {
                    background-color: hsl(var(--primary));
                    color: hsl(var(--primary-foreground));
                  }
                  input[type="file"]::file-selector-button:hover {
                    background-color: hsl(var(--primary) / 0.85);
                  }
                `}</style>
              </div>
        </div>
      </Modal>

      {/* Single file duplicate modal */}
      <DuplicateFileModal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false)
          setPendingFiles([])
          setPendingPnidFileType(null)
        }}
        fileName={duplicateFileName}
        onReplace={handleReplace}
        onKeepBoth={handleKeepBoth}
      />

      {/* Bulk duplicate modal */}
      <BulkDuplicateModal
        isOpen={showBulkDuplicateModal}
        onClose={() => {
          setShowBulkDuplicateModal(false)
          setPendingFiles([])
          setPendingPnidFileType(null)
        }}
        duplicateFiles={duplicateFiles}
        newFilesCount={newFilesCount}
        onReplace={handleBulkReplace}
        onKeepBoth={handleBulkKeepBoth}
        onSkip={handleBulkSkip}
      />
    </>
  )
}

export default UnifiedUploadModal
